import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { getGoogleConnectionStatus, disconnectGoogle } from '../services/api';
import { Button, IconButton, StatusPill, Spinner } from './ui';

interface NotificationOptions {
  title: string;
  message?: string;
  duration?: number;
}

interface GoogleConnectProps {
  user: User | null;
  userOrganization?: string; // 'culturegenesis' or 'partner'
  onSuccess: (options: NotificationOptions) => void;
  onError: (options: NotificationOptions) => void;
  onWarning: (options: NotificationOptions) => void;
}

const GoogleConnect: React.FC<GoogleConnectProps> = ({ user, userOrganization = 'culturegenesis', onSuccess, onError, onWarning }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Don't check connection status if user is not authenticated yet
    if (!user) {
      setLoading(true);
      return;
    }
    // Cached in services/api.ts — won't actually hit the backend on every
    // route transition / Auth remount.
    checkConnectionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Listen for the post-OAuth event dispatched by OAuthCallbackHandler so
  // we update the UI immediately on first connect rather than waiting for
  // the cache TTL to expire.
  useEffect(() => {
    const handleTokensStored = () => {
      console.log('[GoogleConnect] Received google-workspace-connected event, updating UI');
      setConnected(true);
      setLoading(false);
      setTokenExpired(false);

      // Show success notification
      onSuccess({
        title: 'Google Workspace Connected',
        message: 'Successfully connected to Gmail and Google Drive. You can now search emails and files.',
        duration: 6000,
      });
    };

    window.addEventListener('google-workspace-connected', handleTokensStored);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('google-workspace-connected', handleTokensStored);
    };
  }, [onSuccess]); // Re-run if onSuccess callback changes

  const checkConnectionStatus = async () => {
    try {
      console.log('[GoogleConnect] Checking connection status...');
      const status = await getGoogleConnectionStatus();
      console.log('[GoogleConnect] Connection status:', status);
      setConnected(status.connected);

      // Check if token is expired (status includes expiration info from backend)
      if (status.connected && status.expired) {
        setTokenExpired(true);
      } else {
        setTokenExpired(false);
      }
    } catch (error) {
      console.error('[GoogleConnect] Failed to check connection:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    window.location.href = `${backendUrl}/auth/google`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Workspace? You will need to reconnect to use Drive and Gmail features.')) {
      return;
    }

    try {
      console.log('[GoogleConnect] Disconnecting...');
      await disconnectGoogle();
      console.log('[GoogleConnect] Disconnected successfully');
      setConnected(false);
      setTokenExpired(false);

      // Show success notification
      onSuccess({
        title: 'Google Workspace Disconnected',
        message: 'You can reconnect anytime to use Drive and Gmail features.',
        duration: 5000,
      });
    } catch (error) {
      console.error('[GoogleConnect] Failed to disconnect:', error);

      // Show error notification
      onError({
        title: 'Disconnection Failed',
        message: `Unable to disconnect: ${(error as Error).message}`,
        duration: 6000,
      });
    }
  };

  // Partners don't have Google Workspace access - hide this component
  if (userOrganization === 'partner') {
    return null;
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <Spinner size={14} label="Checking…" />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {connected ? (
        <div style={styles.connectedContainer}>
          {tokenExpired ? (
            <>
              <StatusPill variant="pending">Expired</StatusPill>
              <Button variant="primary" size="sm" onClick={handleConnect}>
                Reconnect Google
              </Button>
            </>
          ) : (
            <>
              <StatusPill variant="ok" size="md">Google Connected</StatusPill>
              <IconButton
                label="Reconnect Google"
                size="sm"
                variant="ghost"
                onClick={handleConnect}
                title="Reconnect Google"
              >
                🔄
              </IconButton>
              <IconButton
                label="Disconnect Google"
                size="sm"
                variant="ghost"
                onClick={handleDisconnect}
                title="Disconnect Google"
              >
                ✕
              </IconButton>
            </>
          )}
        </div>
      ) : (
        <div style={styles.disconnectedContainer}>
          <div style={styles.limitedAccessBadge}>
            <span style={styles.limitedAccessIcon}>⚠</span>
            <span style={styles.limitedAccessText}>Limited Access</span>
          </div>
          <Button variant="primary" size="md" onClick={handleConnect}>
            Connect Google Workspace
          </Button>
          <div
            style={styles.infoIconContainer}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <span style={styles.infoIcon}>ℹ️</span>
            {showTooltip && (
              <div style={styles.tooltip}>
                <div style={styles.tooltipHeader}>Why connect?</div>
                <ul style={styles.tooltipList}>
                  <li>Search Gmail & read emails</li>
                  <li>Access Google Drive files</li>
                  <li>View Calendar events</li>
                  <li>Check YouTube analytics</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  connectedContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  disconnectedContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    position: 'relative' as const,
  },
  limitedAccessBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.625rem',
    backgroundColor: 'var(--warning-soft)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--cg-clay-200)',
  },
  limitedAccessIcon: {
    fontSize: '0.75rem',
    color: 'var(--warning)',
  },
  limitedAccessText: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--warning)',
    whiteSpace: 'nowrap' as const,
  },
  infoIconContainer: {
    position: 'relative' as const,
    cursor: 'help',
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    fontSize: '1rem',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
  },
  tooltip: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    marginTop: '0.5rem',
    backgroundColor: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    padding: '0.75rem',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 1000,
    minWidth: '220px',
    animation: 'fadeIn 0.15s ease',
  },
  tooltipHeader: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: 'var(--fg-1)',
    marginBottom: '0.5rem',
  },
  tooltipList: {
    margin: 0,
    padding: '0 0 0 1.25rem',
    fontSize: '0.75rem',
    color: 'var(--fg-2)',
    lineHeight: 1.6,
  },
};

export default GoogleConnect;
